import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  ActionIcon,
  TextInput,
  Badge,
  Paper,
  useTree,
  TreeNodeData,
  Tooltip,
  FileButton,
  Breadcrumbs,
  Anchor,
  Grid,
  Code,
  Switch,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
  IconFolderOpen,
  IconFolderPlus,
  IconFilePlus,
  IconUpload,
  IconDownload,
  IconSearch,
  IconArrowUp,
  IconCheck,
} from '@tabler/icons-react';
import { isTextFileExtension } from '@/Components/MonacoCodeEditor';
import { TreeNav } from './TreeNav';
import { FolderContents } from './FolderContents';
import { FilePreview } from './FilePreview';
import { ItemActionModal } from './ItemActionModal';
import { FileExplorerContext } from './FileExplorerContext';
import {
  VirtualFileExplorerAdapter,
  VirtualFileExplorerModalProps,
  ExplorerModalAction,
  FileExplorerContextValue,
} from './types';
import { matchesAllowedExtensions, getParentPath } from './explorerUtils';

export type { VirtualFileExplorerAdapter, VirtualFileExplorerModalProps };

export function VirtualFileExplorerModal({
  opened,
  onClose,
  title,
  rootLabel = '根目錄',
  files: rawFilesList,
  initialSelectedFile = null,
  adapter,
  extraToolbarActions,
  onFilesUpdated,
  mode = 'manage',
  allowedExtensions = [],
  onSelectConfirm,
}: VirtualFileExplorerModalProps) {
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<string | null>(initialSelectedFile);
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyAllowedExtensions, setOnlyAllowedExtensions] = useState(allowedExtensions.length > 0);

  // Single discriminated union action state replaces separate modals and multiple states
  const [modalAction, setModalAction] = useState<ExplorerModalAction | null>(null);

  const tree = useTree();

  // Drag & drop upload state
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const dragCounter = useRef(0);

  // File preview & editor state
  const [fileTextContent, setFileTextContent] = useState<string>('');
  const [isLoadingFileContent, setIsLoadingFileContent] = useState(false);
  const [isContentModified, setIsContentModified] = useState(false);
  const [isSavingTextFile, setIsSavingTextFile] = useState(false);

  useEffect(() => {
    if (opened) {
      if (initialSelectedFile) {
        setSelectedFile(initialSelectedFile);
        const lastSlash = initialSelectedFile.lastIndexOf('/');
        setSelectedFolder(lastSlash >= 0 ? initialSelectedFile.slice(0, lastSlash) : '');
      }
      setOnlyAllowedExtensions(allowedExtensions.length > 0);
    } else {
      setSelectedFolder('');
      setSelectedFile(null);
      setSearchQuery('');
      setFileTextContent('');
      setIsContentModified(false);
      setModalAction(null);
    }
  }, [opened, initialSelectedFile, allowedExtensions.length]);

  const rawFiles = useMemo(() => {
    return (rawFilesList || []).filter(Boolean);
  }, [rawFilesList]);

  // Build complete directory set
  const allFolders = useMemo(() => {
    const folderSet = new Set<string>();
    folderSet.add('');

    rawFiles.forEach((raw) => {
      const clean = raw.trim().replace(/^\/+/, '');
      if (clean.endsWith('/')) {
        const f = clean.slice(0, -1);
        if (f) folderSet.add(f);
      }
      const parts = clean.split('/').filter(Boolean);
      if (parts.length > 1) {
        let current = '';
        for (let i = 0; i < parts.length - 1; i++) {
          current = current ? `${current}/${parts[i]}` : parts[i];
          folderSet.add(current);
        }
      }
    });

    return Array.from(folderSet).sort((a, b) => a.localeCompare(b));
  }, [rawFiles]);

  const regularFiles = useMemo(() => {
    return rawFiles
      .map((f) => f.trim().replace(/^\/+/, ''))
      .filter((f) => f && !f.endsWith('/'));
  }, [rawFiles]);

  // Mantine Tree data
  const folderTreeData: TreeNodeData[] = useMemo(() => {
    const map = new Map<string, TreeNodeData>();

    const rootNode: TreeNodeData = {
      label: rootLabel,
      value: '',
      children: [],
    };
    map.set('', rootNode);

    allFolders
      .filter((f) => f !== '')
      .forEach((folderPath) => {
        const parts = folderPath.split('/');
        const name = parts[parts.length - 1];
        const parent = parts.slice(0, -1).join('/');

        const node: TreeNodeData = {
          label: name,
          value: folderPath,
          children: [],
        };
        map.set(folderPath, node);

        const parentNode = map.get(parent) || rootNode;
        parentNode.children = parentNode.children || [];
        parentNode.children.push(node);
      });

    return [rootNode];
  }, [allFolders, rootLabel]);

  // Current view items (folders + files) with extension allowance
  const currentViewItems = useMemo(() => {
    const checkAllowed = (f: string) => matchesAllowedExtensions(f, allowedExtensions);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      let matched = regularFiles.filter((f) => f.toLowerCase().includes(q));
      if (onlyAllowedExtensions && allowedExtensions.length > 0) {
        matched = matched.filter(checkAllowed);
      }

      return {
        isSearchMode: true,
        subfolders: [] as string[],
        files: matched.map((fullPath) => ({
          fullPath,
          name: fullPath,
          isAllowed: checkAllowed(fullPath),
        })),
      };
    }

    const currentPrefix = selectedFolder ? `${selectedFolder}/` : '';

    const subfolderSet = new Set<string>();
    allFolders.forEach((f) => {
      if (!f) return;
      if (selectedFolder === '') {
        if (!f.includes('/')) subfolderSet.add(f);
      } else if (f.startsWith(currentPrefix)) {
        const remainder = f.slice(currentPrefix.length);
        if (remainder && !remainder.includes('/')) {
          subfolderSet.add(f);
        }
      }
    });

    let immediateFiles: { fullPath: string; name: string; isAllowed: boolean }[] = [];
    regularFiles.forEach((fullPath) => {
      if (selectedFolder === '') {
        if (!fullPath.includes('/')) {
          immediateFiles.push({
            fullPath,
            name: fullPath,
            isAllowed: checkAllowed(fullPath),
          });
        }
      } else if (fullPath.startsWith(currentPrefix)) {
        const remainder = fullPath.slice(currentPrefix.length);
        if (remainder && !remainder.includes('/')) {
          immediateFiles.push({
            fullPath,
            name: remainder,
            isAllowed: checkAllowed(fullPath),
          });
        }
      }
    });

    if (onlyAllowedExtensions && allowedExtensions.length > 0) {
      immediateFiles = immediateFiles.filter((f) => f.isAllowed);
    }

    return {
      isSearchMode: false,
      subfolders: Array.from(subfolderSet).sort((a, b) => a.localeCompare(b)),
      files: immediateFiles.sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [selectedFolder, searchQuery, allFolders, regularFiles, allowedExtensions, onlyAllowedExtensions]);

  // Auto-expand tree nodes
  useEffect(() => {
    if (opened) {
      tree.expandAllNodes();
    }
  }, [opened, folderTreeData]);

  // Load content when selected file changes
  useEffect(() => {
    if (!selectedFile) {
      setFileTextContent('');
      setIsContentModified(false);
      return;
    }

    if (isTextFileExtension(selectedFile)) {
      let isCancelled = false;
      setIsLoadingFileContent(true);
      setIsContentModified(false);

      adapter
        .getFileContent(selectedFile)
        .then((content) => {
          if (!isCancelled) {
            setFileTextContent(content);
          }
        })
        .catch(() => {
          if (!isCancelled) {
            setFileTextContent('');
          }
        })
        .finally(() => {
          if (!isCancelled) {
            setIsLoadingFileContent(false);
          }
        });

      return () => {
        isCancelled = true;
      };
    } else {
      setFileTextContent('');
      setIsContentModified(false);
    }
  }, [selectedFile, adapter]);

  // Drag & drop file extraction
  const extractFilesFromDataTransfer = async (
    items: DataTransferItemList | null,
    filesList: FileList | null
  ): Promise<{ file: File; relativePath: string }[]> => {
    const results: { file: File; relativePath: string }[] = [];

    if (items && items.length > 0) {
      async function traverseEntry(entry: any, currentPath = '') {
        if (entry.isFile) {
          const file: File = await new Promise((resolve, reject) => entry.file(resolve, reject));
          results.push({
            file,
            relativePath: currentPath ? `${currentPath}/${file.name}` : file.name,
          });
        } else if (entry.isDirectory) {
          const dirReader = entry.createReader();
          const readEntries = (): Promise<any[]> =>
            new Promise((resolve, reject) => {
              dirReader.readEntries(resolve, reject);
            });

          let entries: any[] = [];
          let batch: any[] = await readEntries();
          while (batch.length > 0) {
            entries = entries.concat(batch);
            batch = await readEntries();
          }

          const newPath = currentPath ? `${currentPath}/${entry.name}` : entry.name;
          for (const child of entries) {
            await traverseEntry(child, newPath);
          }
        }
      }

      const entries: any[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
          if (entry) {
            entries.push(entry);
          } else {
            const file = item.getAsFile();
            if (file) {
              results.push({ file, relativePath: file.name });
            }
          }
        }
      }

      for (const entry of entries) {
        await traverseEntry(entry);
      }
    } else if (filesList && filesList.length > 0) {
      for (let i = 0; i < filesList.length; i++) {
        const file = filesList[i];
        results.push({
          file,
          relativePath: (file as any).webkitRelativePath || file.name,
        });
      }
    }

    return results;
  };

  const handleUploadItems = async (
    items: { file: File; relativePath: string }[],
    targetFolder: string = selectedFolder
  ) => {
    if (!items || items.length === 0) return;

    setIsUploading(true);
    try {
      for (const item of items) {
        const fullDestPath = targetFolder ? `${targetFolder}/${item.relativePath}` : item.relativePath;
        await adapter.uploadFile(fullDestPath, item.file);
      }

      notifications.show({
        title: '上傳成功',
        message: `已成功上傳 ${items.length} 個檔案至 ${targetFolder || '根目錄'}`,
        color: 'teal',
        icon: <IconCheck size={16} />,
      });

      onFilesUpdated?.();
    } catch (err: any) {
      notifications.show({
        title: '上傳失敗',
        message: err?.message || '檔案上傳失敗，請稍後再試',
        color: 'red',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadFiles = async (pickedFiles: File[] | null, specificDestPath?: string) => {
    if (!pickedFiles || pickedFiles.length === 0) return;
    if (specificDestPath) {
      setIsUploading(true);
      try {
        await adapter.uploadFile(specificDestPath, pickedFiles[0]);
        notifications.show({
          title: '覆蓋成功',
          message: `已成功覆蓋檔案 ${specificDestPath}`,
          color: 'teal',
          icon: <IconCheck size={16} />,
        });
        onFilesUpdated?.();
      } catch (err: any) {
        notifications.show({
          title: '覆蓋失敗',
          message: err?.message || '檔案覆蓋失敗，請稍後再試',
          color: 'red',
        });
      } finally {
        setIsUploading(false);
      }
      return;
    }

    const items = pickedFiles.map((file) => ({
      file,
      relativePath: file.name,
    }));
    await handleUploadItems(items, selectedFolder);
  };

  // Drag & drop listeners
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      setIsDraggingOver(false);
      dragCounter.current = 0;
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingOver) {
      setIsDraggingOver(true);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetFolder?: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    dragCounter.current = 0;

    const items = await extractFilesFromDataTransfer(e.dataTransfer.items, e.dataTransfer.files);
    if (items.length > 0) {
      await handleUploadItems(items, targetFolder ?? selectedFolder);
    }
  };

  // Unified Item Action Submit Handler
  const handleConfirmModalAction = async (action: ExplorerModalAction, newName: string) => {
    try {
      if (action.type === 'createFile') {
        const fullFilePath = action.targetFolder ? `${action.targetFolder}/${newName}` : newName;
        await adapter.saveFile(fullFilePath, '');
        notifications.show({
          title: '建立成功',
          message: `已建立檔案 ${newName}`,
          color: 'teal',
          icon: <IconCheck size={16} />,
        });
        setSelectedFile(fullFilePath);
      } else if (action.type === 'createFolder') {
        const fullFolderPath = action.targetFolder ? `${action.targetFolder}/${newName}` : newName;
        await adapter.createFolder(fullFolderPath);
        notifications.show({
          title: '建立成功',
          message: `已建立資料夾 ${newName}`,
          color: 'teal',
          icon: <IconCheck size={16} />,
        });
        setSelectedFolder(fullFolderPath);
      } else if (action.type === 'rename') {
        if (action.itemType === 'file') {
          await adapter.renameFile(action.currentPath, newName);
          const parent = getParentPath(action.currentPath);
          const newPath = parent ? `${parent}/${newName}` : newName;
          if (selectedFile === action.currentPath) {
            setSelectedFile(newPath);
          }
          notifications.show({
            title: '重新命名成功',
            message: `已將檔案重新命名為 ${newName}`,
            color: 'teal',
            icon: <IconCheck size={16} />,
          });
        } else {
          await adapter.renameFolder(action.currentPath, newName);
          const parent = getParentPath(action.currentPath);
          const newFolderPath = parent ? `${parent}/${newName}` : newName;
          if (selectedFolder === action.currentPath || selectedFolder.startsWith(`${action.currentPath}/`)) {
            const suffix = selectedFolder.slice(action.currentPath.length);
            setSelectedFolder(`${newFolderPath}${suffix}`);
          }
          if (selectedFile && selectedFile.startsWith(`${action.currentPath}/`)) {
            const suffix = selectedFile.slice(action.currentPath.length);
            setSelectedFile(`${newFolderPath}${suffix}`);
          }
          notifications.show({
            title: '重新命名成功',
            message: `已將資料夾重新命名為 ${newName}`,
            color: 'teal',
            icon: <IconCheck size={16} />,
          });
        }
      } else if (action.type === 'duplicate') {
        if (action.itemType === 'file') {
          await adapter.duplicateFile(action.currentPath, newName);
          notifications.show({
            title: '複製成功',
            message: `已建立檔案副本 ${newName}`,
            color: 'teal',
            icon: <IconCheck size={16} />,
          });
        } else {
          await adapter.duplicateFolder(action.currentPath, newName);
          notifications.show({
            title: '複製成功',
            message: `已建立資料夾副本 ${newName}`,
            color: 'teal',
            icon: <IconCheck size={16} />,
          });
        }
      }

      setModalAction(null);
      onFilesUpdated?.();
    } catch (err: any) {
      notifications.show({
        title: '操作失敗',
        message: err?.message || '執行操作失敗，請稍後再試',
        color: 'red',
      });
    }
  };

  const handleDeleteFolder = (folderPath: string, e?: React.MouseEvent) => {
    e?.stopPropagation();

    modals.openConfirmModal({
      title: '確認刪除資料夾',
      centered: true,
      children: (
        <Text size="sm">
          確定要刪除資料夾 <Code>{folderPath}</Code> 及其內部的所有檔案嗎？此操作無法復原。
        </Text>
      ),
      labels: { confirm: '確認刪除', cancel: '取消' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await adapter.deleteFolder(folderPath);

          notifications.show({
            title: '刪除成功',
            message: `已刪除資料夾 ${folderPath}`,
            color: 'teal',
            icon: <IconCheck size={16} />,
          });

          if (selectedFolder === folderPath || selectedFolder.startsWith(`${folderPath}/`)) {
            setSelectedFolder(getParentPath(folderPath));
          }
          if (selectedFile && selectedFile.startsWith(`${folderPath}/`)) {
            setSelectedFile(null);
          }

          onFilesUpdated?.();
        } catch (err: any) {
          notifications.show({
            title: '刪除失敗',
            message: err?.message || '刪除資料夾失敗',
            color: 'red',
          });
        }
      },
    });
  };

  const handleDeleteFile = (filePath: string, e?: React.MouseEvent) => {
    e?.stopPropagation();

    modals.openConfirmModal({
      title: '確認刪除檔案',
      centered: true,
      children: (
        <Text size="sm">
          確定要刪除檔案 <Code>{filePath}</Code> 嗎？此操作無法復原。
        </Text>
      ),
      labels: { confirm: '確認刪除', cancel: '取消' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await adapter.deleteFile(filePath);

          notifications.show({
            title: '刪除成功',
            message: `已刪除檔案 ${filePath}`,
            color: 'teal',
            icon: <IconCheck size={16} />,
          });

          if (selectedFile === filePath) {
            setSelectedFile(null);
          }

          onFilesUpdated?.();
        } catch (err: any) {
          notifications.show({
            title: '刪除失敗',
            message: err?.message || '刪除檔案失敗',
            color: 'red',
          });
        }
      },
    });
  };

  const handleSaveTextFile = async () => {
    if (!selectedFile) return;

    setIsSavingTextFile(true);
    try {
      await adapter.saveFile(selectedFile, fileTextContent);
      setIsContentModified(false);
      notifications.show({
        title: '儲存成功',
        message: `已儲存 ${selectedFile}`,
        color: 'teal',
        icon: <IconCheck size={16} />,
      });
      onFilesUpdated?.();
    } catch (err: any) {
      notifications.show({
        title: '儲存失敗',
        message: err?.message || '儲存檔案失敗',
        color: 'red',
      });
    } finally {
      setIsSavingTextFile(false);
    }
  };

  const handleConfirmPick = (filePath: string) => {
    if (!matchesAllowedExtensions(filePath, allowedExtensions)) return;
    onSelectConfirm?.(filePath);
    onClose();
  };

  // Breadcrumbs
  const breadcrumbItems = useMemo(() => {
    const items = [
      <Anchor
        key="root"
        size="xs"
        onClick={() => {
          setSelectedFolder('');
          setSearchQuery('');
        }}
        style={{ cursor: 'pointer', fontWeight: selectedFolder === '' ? 600 : 400 }}
      >
        {rootLabel}
      </Anchor>,
    ];

    if (selectedFolder) {
      const parts = selectedFolder.split('/').filter(Boolean);
      let accumulated = '';
      parts.forEach((part, index) => {
        accumulated = accumulated ? `${accumulated}/${part}` : part;
        const currentTarget = accumulated;
        const isLast = index === parts.length - 1;
        items.push(
          isLast ? (
            <Text key={currentTarget} size="xs" fw={600}>
              {part}
            </Text>
          ) : (
            <Anchor
              key={currentTarget}
              size="xs"
              onClick={() => {
                setSelectedFolder(currentTarget);
                setSearchQuery('');
              }}
              style={{ cursor: 'pointer' }}
            >
              {part}
            </Anchor>
          )
        );
      });
    }

    return items;
  }, [selectedFolder, rootLabel]);

  const handleGoUp = () => {
    if (!selectedFolder) return;
    setSelectedFolder(getParentPath(selectedFolder));
    setSearchQuery('');
  };

  // Context value bundle
  const contextValue: FileExplorerContextValue = {
    mode,
    allowedExtensions,
    onlyAllowedExtensions,
    setOnlyAllowedExtensions,
    selectedFolder,
    setSelectedFolder,
    selectedFile,
    setSelectedFile,
    searchQuery,
    setSearchQuery,
    tree,
    folderTreeData,
    currentViewItems,
    allFolders,
    regularFiles,
    adapter,
    isUploading,
    isDraggingOver,
    onUploadFiles: handleUploadFiles,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
    openActionModal: (action) => setModalAction(action),
    onDeleteFolder: handleDeleteFolder,
    onDeleteFile: handleDeleteFile,
    onConfirmPick: handleConfirmPick,
    fileTextContent,
    isLoadingFileContent,
    isContentModified,
    isSavingTextFile,
    onContentChange: (val) => {
      setFileTextContent(val);
      setIsContentModified(true);
    },
    onSaveTextFile: handleSaveTextFile,
    extraToolbarActions,
  };

  return (
    <FileExplorerContext.Provider value={contextValue}>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Group gap="xs">
            <IconFolderOpen size={22} color="var(--mantine-color-blue-6)" />
            {typeof title === 'string' ? (
              <Text fw={600} size="md">
                {title}
              </Text>
            ) : (
              title
            )}
            <Badge variant="light" size="sm">
              {regularFiles.length} 個檔案
            </Badge>
            {mode === 'pick' && (
              <Badge color="cyan" size="sm" variant="filled">
                挑選模式
              </Badge>
            )}
          </Group>
        }
        size="92vw"
        styles={{
          body: {
            height: 'calc(90vh - 60px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          content: {
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        centered
      >
        <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
          {/* Top Global Toolbar */}
          <Paper withBorder p="xs" radius="md" style={{ flexShrink: 0 }}>
            <Group justify="space-between" align="center" wrap="wrap">
              <Group gap="xs">
                {selectedFolder !== '' && (
                  <Button
                    variant="subtle"
                    size="xs"
                    leftSection={<IconArrowUp size={14} />}
                    onClick={handleGoUp}
                  >
                    返回上一層
                  </Button>
                )}
                <Breadcrumbs separator="/" separatorMargin="xs">
                  {breadcrumbItems}
                </Breadcrumbs>
              </Group>

              <Group gap="xs">
                {allowedExtensions.length > 0 && (
                  <Switch
                    size="xs"
                    label="僅顯示符合格式"
                    checked={onlyAllowedExtensions}
                    onChange={(e) => setOnlyAllowedExtensions(e.currentTarget.checked)}
                  />
                )}

                <TextInput
                  size="xs"
                  placeholder="搜尋檔案名稱..."
                  leftSection={<IconSearch size={14} />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  style={{ width: '180px' }}
                />

                {mode === 'manage' && (
                  <>
                    <Button
                      variant="light"
                      color="teal"
                      size="xs"
                      leftSection={<IconFilePlus size={14} />}
                      onClick={() => setModalAction({ type: 'createFile', targetFolder: selectedFolder })}
                    >
                      新增檔案
                    </Button>

                    <Button
                      variant="light"
                      size="xs"
                      leftSection={<IconFolderPlus size={14} />}
                      onClick={() => setModalAction({ type: 'createFolder', targetFolder: selectedFolder })}
                    >
                      新增資料夾
                    </Button>
                  </>
                )}

                <FileButton
                  onChange={(picked) => handleUploadFiles(picked ? Array.from(picked) : null)}
                  multiple
                >
                  {(props) => (
                    <Button
                      {...props}
                      leftSection={<IconUpload size={14} />}
                      size="xs"
                      loading={adapter.isOperating || isUploading}
                    >
                      上傳檔案至此
                    </Button>
                  )}
                </FileButton>

                {adapter.getDownloadZipUrl && (
                  <Tooltip label="打包下載為 .zip">
                    <ActionIcon
                      component="a"
                      href={adapter.getDownloadZipUrl()}
                      download={adapter.downloadZipFileName || 'download.zip'}
                      variant="light"
                      color="blue"
                      size="md"
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}

                {extraToolbarActions}
              </Group>
            </Group>
          </Paper>

          {/* Adaptive Layout: If selectedFile is present, 3-column (3:4.5:4.5). If not, 2-column (3:9). */}
          <Grid
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
            styles={{ inner: { flex: 1, minHeight: 0, height: '100%' } }}
          >
            {/* Column 1: Tree Navigation */}
            <Grid.Col
              span={{ base: 12, md: 3 }}
              style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
              <TreeNav />
            </Grid.Col>

            {/* Column 2: Folder Contents */}
            <Grid.Col
              span={{ base: 12, md: selectedFile ? 4.5 : 9 }}
              style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
              <FolderContents />
            </Grid.Col>

            {/* Column 3: Live Preview / Code Editor (Adaptive collapse) */}
            {selectedFile && (
              <Grid.Col
                span={{ base: 12, md: 4.5 }}
                style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
              >
                <FilePreview />
              </Grid.Col>
            )}
          </Grid>
        </Stack>
      </Modal>

      {/* Unified Action Modal */}
      <ItemActionModal
        action={modalAction}
        onClose={() => setModalAction(null)}
        onConfirm={handleConfirmModalAction}
        isLoading={!!adapter.isOperating}
        allFolders={allFolders}
        regularFiles={regularFiles}
      />
    </FileExplorerContext.Provider>
  );
}
